/**
 * Outliers — audits in the last N hours whose `total_cost` exceeded
 * the configurable threshold. Row click reveals the full per-audit
 * `infra` breakdown JSON (`/admin/audit-spend/outlier-detail/:auditId`).
 */
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { ViewQueryButton } from './ViewQueryButton';
import { formatDuration, formatLocal, formatUsd } from './format';
import type { ApiEnvelope, OutlierDetailData, OutliersData } from './types';

interface OutliersTableProps {
  envelope: ApiEnvelope<OutliersData> | undefined;
  loading: boolean;
  threshold: number;
  onThresholdChange: (n: number) => void;
}

function OutlierDetail({ auditId }: { auditId: string }) {
  const { data, isLoading, isError } = useQuery<ApiEnvelope<OutlierDetailData>>({
    queryKey: ['audit-spend', 'outlier-detail', auditId],
    queryFn: () => api.getAuditSpendOutlierDetail(auditId),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="px-3 py-2 text-xs text-zinc-500">loading…</div>;
  if (isError) return <div className="px-3 py-2 text-xs text-red-500">failed to load detail</div>;
  if (!data?.data.found) {
    return (
      <div className="px-3 py-2 text-xs text-zinc-500">
        Detail not found (row may be outside the 7-day raw-log window).
      </div>
    );
  }
  return (
    <pre className="px-3 py-2 max-h-72 overflow-auto text-[11px] font-mono text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950">
      {JSON.stringify(data.data.data, null, 2)}
    </pre>
  );
}

export function OutliersTable({
  envelope,
  loading,
  threshold,
  onThresholdChange,
}: OutliersTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const rows = envelope?.data.rows ?? [];

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Cost outliers (last 24h)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Audits whose total cost exceeded the threshold.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">Threshold $</label>
          <input
            type="number"
            min={0}
            step={0.05}
            value={threshold}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              if (Number.isFinite(v) && v >= 0) onThresholdChange(v);
            }}
            className="w-20 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-xs"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500">loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-500">
          No audits above ${threshold.toFixed(2)} in the last 24h.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-6" />
                <th className="text-left px-3 py-2">audit_id</th>
                <th className="text-left px-3 py-2">source</th>
                <th className="text-right px-3 py-2">total</th>
                <th className="text-left px-3 py-2">top line</th>
                <th className="text-right px-3 py-2">duration</th>
                <th className="text-left px-3 py-2">when</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = expanded === r.audit_id;
                return (
                  <>
                    <tr
                      key={r.audit_id}
                      className="border-t border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 cursor-pointer"
                      onClick={() => setExpanded(open ? null : r.audit_id)}
                    >
                      <td className="px-3 py-2 text-zinc-500">
                        {open ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.audit_id}</td>
                      <td className="px-3 py-2 text-xs">{r.source}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {formatUsd(r.total_cost)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5">
                          {r.reason}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-zinc-500">
                        {formatDuration(r.duration_ms)}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500">
                        {formatLocal(r.publish_time)}
                      </td>
                    </tr>
                    {open && (
                      <tr
                        key={`${r.audit_id}-detail`}
                        className="border-t border-zinc-200 dark:border-zinc-700"
                      >
                        <td colSpan={7} className="bg-zinc-50 dark:bg-zinc-950">
                          <OutlierDetail auditId={r.audit_id} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-4 py-2 border-t border-zinc-200 dark:border-zinc-700">
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
